<script lang="ts">
	import * as Command from '$lib/components/ui/command';
	import LoaderCircle from '@lucide/svelte/icons/loader-circle';
	import Search from '@lucide/svelte/icons/search';
	import * as Dialog from '$lib/components/ui/dialog';
	import type { SelectCourse, SelectCourseCategory } from '$db/tables';
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { Button } from '../ui/button';
	import { onMount } from 'svelte';

	let open = $state(false);
	let value = $state('');
	let loading = $state(false);
	let filter = $state(true);

	// add a proper result type for this
	let results = $state<
		{
			course: SelectCourse;
			category: SelectCourseCategory;
		}[]
	>([]);

	onMount(() => {
		function handleKeydown(e: KeyboardEvent) {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				open = !open;
			}
		}

		document.addEventListener('keydown', handleKeydown);

		return () => {
			document.removeEventListener('keydown', handleKeydown);
		};
	});

	function runCommand(cmd: () => void) {
		open = false;
		cmd();
	}

	async function search(searchValue: string) {
		const response = await fetch('/api/course-search', {
			method: 'POST',
			body: JSON.stringify({ searchValue }),
			headers: {
				'content-type': 'application/json'
			}
		});

		if (value) {
			results = await response.json();
		}
		loading = false;
	}

	let timer: ReturnType<typeof setTimeout>;

	const debounce = (v: string) => {
		if (v.length > 1) {
			loading = true;
			clearTimeout(timer);
			timer = setTimeout(() => {
				v = value;
				search(v);
			}, 1000);
		}
	};

	$effect(() => {
		if (browser && value) {
			debounce(value);
		}
	});

	$effect(() => {
		if (!value) {
			results = [];
			filter = true;
		}
	});

	$effect(() => {
		if (results.length > 0) {
			filter = false;
		} else {
			filter = true;
		}
	});
</script>

<Button variant="secondary" size="icon" onclick={() => (open = !open)}
	><Search class="size-5" /><span class="sr-only">Kurssuche</span></Button
>

<Dialog.Root bind:open>
	<Dialog.Content class="p-0">
		{#key filter}
			<Command.Root shouldFilter={filter} class="cmdk h-64">
				<Command.Input bind:value autofocus placeholder="Suche nach einem Kurs..." />
				<Command.List class="h-full overflow-auto overscroll-contain">
					{#if results.length > 0}
						{#if loading}
							<Command.Loading class="flex justify-center py-6"
								><LoaderCircle class="animate-spin text-muted-foreground" /></Command.Loading
							>
						{:else}
							<Command.Empty>Keine Ergebnisse.</Command.Empty>
						{/if}
						{#key loading}
							<Command.Group heading="Ergebnisse">
								{#each results as result}
									<Command.Item
										class="flex justify-between"
										onSelect={() =>
											runCommand(() =>
												goto(`/ausbildungen/${result.category.slug}/${result.course.slug}`)
											)}
									>
										<div>
											{result.course.name}
										</div>
										<div class="text-sm text-muted-foreground">
											<span class="sr-only">
												{result.course.id}
											</span>
										</div>
									</Command.Item>
								{/each}
							</Command.Group>
						{/key}
					{:else if loading}
						<Command.Loading class="flex justify-center py-6"
							><LoaderCircle class="animate-spin text-muted-foreground" /></Command.Loading
						>
					{:else}
						<Command.Empty>Keine Ergebnisse.</Command.Empty>
					{/if}
				</Command.List>
			</Command.Root>
		{/key}
	</Dialog.Content>
</Dialog.Root>
